import React, { memo, useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import ProjectCard from "./ProjectCards";
import { useTranslation } from "react-i18next";
import SmartUnderlineTitle from "../SmartUnderline/SmartUnderline";
import "./Projects.css";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";

// Imports des images (le navigateur commencera à les charger dès qu'il verra les balises <img>)
import atpTour from "./media/atpTour.webp";
import transport from "./media/transport.webp";
import variance from "./media/reduction_variance.webp";
import anderson from "./media/anderson.webp";
import signal from "./media/signal.webp";
import javafx from "./media/javafx.webp";
import puzzle from "./media/puzzle.webp";

const Projects = memo(() => {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language === "en";

  const [isMobile, setIsMobile] = useState(false);
  const [activeIndex, setActiveIndex] = useState(1);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const projectData = [
    {
      imgPath: javafx,
      title: (
        <span className="project-title">
          {t("javafx_title")} {isEnglish && <span className="star"><sup>&#9733;</sup></span>}
        </span>
      ),
      technologies: ["Java", "MySQL", "Docker", "JavaFX"],
      description: t("javafx_desc"),
      ghLink: "https://github.com/rmdair/Travel-Agency-Data-Manager",
      links: { fr: "fr/dev/gestion-donnees-reservations.pdf", en: "en/dev/reservation-data-management.pdf" }
    },
    {
      imgPath: transport,
      title: <span className="project-title">{t("transport_title")}</span>,
      technologies: ["AMPL"],
      description: t("transport_desc"),
      ghLink: "https://github.com/rmdair/Supply-Chain-Network-Optimization",
      demoLink: "fr/pratique/Optimisation-Reseau-Logistique-AMPL.pdf",
    },
    {
      imgPath: atpTour,
      title: <span className="project-title">{t("atp_tour_title")}</span>,
      technologies: ["PHP", "JavaScript", "MySQL"],
      description: t("atp_tour_desc"),
      projectLink: "projet_simulation_ATP_Tour/index.php",
    },
    {
      imgPath: variance,
      title: (
        <span className="project-title">
          {t("variance_title")} {isEnglish && <span className="star"><sup>&#9733;</sup></span>}
        </span>
      ),
      technologies: ["R Language"],
      description: t("variance_desc"),
      ghLink: "https://github.com/rmdair/Monte-Carlo-Variance-Reduction",
      links: { fr: "fr/theorie/Reduction-Variance-Monte-Carlo.pdf", en: "en/theory/Monte-Carlo-Variance-Reduction.pdf" }
    },
    {
      imgPath: puzzle,
      title: <span className="project-title">{t("missing_data_title")}</span>,
      technologies: ["R Language"],
      description: t("missing_data_desc"),
      ghLink: "https://github.com/rmdair/Missing-Data-Imputation",
      demoLink: "fr/theorie/Rapport-Imputation-Donnees-Manquantes.pdf",
      beamerLink: "fr/theorie/Slides-Imputation-Donnees-Manquantes.pdf",
    },
    {
      imgPath: anderson,
      title: (
        <span className="project-title">
          {t("anderson_title")} {isEnglish && <span className="star"><sup>&#9733;</sup></span>}
        </span>
      ),
      technologies: ["Python"],
      description: t("anderson_desc"),
      ghLink: "https://github.com/rmdair/Anderson-Darling-Goodness-of-Fit",
      links: { fr: "fr/theorie/Test-Anderson-Darling-Etude.pdf", en: "en/theory/Anderson-Darling-Test-Study.pdf" }
    },
    {
      imgPath: signal,
      title: <span className="project-title">{t("signal_title")}</span>,
      technologies: ["Python"],
      description: t("signal_desc"),
      ghLink: "https://github.com/rmdair/Digital-Signal-Analysis",   
      demoLink: "fr/pratique/echantillonnage-et-filtrage.pdf",
    },
  ];

  return (
    <Container fluid className="project-section reveal-active">
      <Container>
        <div className="reveal-content">
          {/* On passe isReady={true} car on veut que le titre soit interactif immédiatement */}
          <SmartUnderlineTitle isReady={true} className="project-heading">
            <span className="purple">{t("projects_title")}</span>
          </SmartUnderlineTitle>

          {isEnglish && (
            <h3 className="english-text" style={{ fontSize: "1.1em", marginTop: "20px", textAlign: "center", opacity: 0.9 }}>
                Projects with an available English translation are marked with a 
                <span className="star"><sup> &#9733; </sup></span> 
                in their title.
            </h3>
          )}

          {isMobile ? (
            <div className="swiper-mobile-container">
              <Swiper
                loop={true}
                grabCursor={true}
                centeredSlides={true}
                slidesPerView={1}
                spaceBetween={30}
                navigation={{
                  nextEl: '.swiper-button-next-custom',
                  prevEl: '.swiper-button-prev-custom',
                }}
                onSlideChange={(swiper) => setActiveIndex(swiper.realIndex + 1)}
                modules={[Navigation, Autoplay]}
                className="project-swiper"
              >
                {projectData.map((project, index) => (
                  <SwiperSlide key={index} className="project-swiper-slide">
                    {/* priority={index === 0} charge l'image du premier slide immédiatement */}
                    <ProjectCard {...project} priority={index === 0} />
                  </SwiperSlide>
                ))}
              </Swiper>
              
              <div className="custom-navigation-container">
                <button className="swiper-button-prev-custom" aria-label="Previous">
                  <svg viewBox="0 0 24 24" width="28" height="28" stroke="currentColor" strokeWidth="2" fill="none"><path d="M15 18l-6-6 6-6"/></svg>
                </button>
                <div className="project-counter">
                  {t("project_counter", { current: activeIndex, total: projectData.length })}
                </div>
                <button className="swiper-button-next-custom" aria-label="Next">
                  <svg viewBox="0 0 24 24" width="28" height="28" stroke="currentColor" strokeWidth="2" fill="none"><path d="M9 18l6-6-6-6"/></svg>
                </button>
              </div>
            </div>
          ) : (
            <Row className="project-row-custom" style={{ justifyContent: "center", paddingBottom: "25px" }}>
              {projectData.map((project, index) => (
                <Col 
                  key={index} 
                  className="project-card" 
                  // Animation de cascade purement CSS via les délais
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {/* priority={index < 3} charge les 3 premières images (visibles à l'écran) en priorité haute */}
                  <ProjectCard {...project} priority={index < 3} />
                </Col>
              ))}
            </Row>
          )}
        </div>
      </Container>
    </Container>
  );
});

export default Projects;